// Suppliers (spec/13 §6.4; read with `inventory:read`, kept with `inventory:manage`): who the venue buys from — a name,
// a phone, an email and a note — picked when a delivery is received. Archiving one keeps its deliveries and takes it
// off the delivery form.

import type { StockSupplier, UpdateStockSupplierRequest } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, Checkbox, Dialog, Switch, Table, TextField, useToast, type TableColumn } from '@clavian/ui-web';
import { useState, type FormEvent, type ReactNode } from 'react';

import { LoadFailure } from '../../shell/LoadFailure';
import { Icon } from '../../shell/nav-icons';
import { useCurrentVenue } from '../../shell/scope';
import { Notice, TableSkeleton, Toolbar, ToolbarSpring } from '../../ui';
import type { StockAccess } from './access';
import { NO_FIGURE } from './format';
import { useCreateSupplier, useStockSuppliers, useUpdateSupplier } from './queries';
import { StockEmpty } from './StockPage';
import { useProblem } from '../../api/use-problem';

export function SuppliersTab({ access }: { readonly access: StockAccess }): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const [showArchived, setShowArchived] = useState(false);
  const suppliers = useStockSuppliers(venue.organizationId, venue.id, { includeArchived: showArchived });
  const [editing, setEditing] = useState<StockSupplier | 'new' | null>(null);

  const columns: TableColumn<StockSupplier>[] = [
    {
      key: 'name',
      header: t('stock.suppliers.name'),
      cell: (supplier) =>
        access.manage ? (
          <button type="button" className="console-cell-link" onClick={() => setEditing(supplier)}>
            <span>{supplier.name}</span>
            {supplier.archived ? <span className="console-cell-sub">{t('stock.items.archived')}</span> : null}
          </button>
        ) : (
          <span>
            {supplier.name} {supplier.archived ? <Badge tone="neutral">{t('stock.items.archived')}</Badge> : null}
          </span>
        ),
    },
    { key: 'phone', header: t('stock.suppliers.phone'), cell: (supplier) => <span className="console-num">{supplier.phone ?? NO_FIGURE}</span> },
    { key: 'email', header: t('stock.suppliers.email'), cell: (supplier) => supplier.email ?? NO_FIGURE },
    { key: 'note', header: t('stock.suppliers.note'), cell: (supplier) => <span className="console-muted">{supplier.note ?? ''}</span> },
  ];

  let body: ReactNode;
  if (suppliers.data === undefined) {
    body = suppliers.isError ? <LoadFailure error={suppliers.error} message={t('stock.suppliers.unavailable')} onRetry={() => void suppliers.refetch()} /> : <TableSkeleton rows={4} />;
  } else if (suppliers.data.length === 0 && !showArchived) {
    body = (
      <StockEmpty
        title={t('stock.suppliers.emptyTitle')}
        description={t('stock.suppliers.empty')}
        action={
          access.manage ? (
            <Button variant="primary" icon={<Icon name="plus" />} onClick={() => setEditing('new')}>
              {t('stock.suppliers.add')}
            </Button>
          ) : undefined
        }
      />
    );
  } else {
    body = (
      <div className="console-table-card">
        <Table caption={t('stock.suppliers.caption')} captionHidden columns={columns} rows={suppliers.data} rowKey={(supplier) => supplier.id} empty={t('stock.suppliers.empty')} />
      </div>
    );
  }

  return (
    <div className="console-stock-tab">
      <Toolbar label={t('stock.suppliers.toolbar')}>
        <Checkbox label={t('stock.suppliers.showArchived')} checked={showArchived} onCheckedChange={(checked) => setShowArchived(checked === true)} />
        <ToolbarSpring />
        {access.manage ? (
          <Button variant="secondary" size="sm" icon={<Icon name="plus" />} onClick={() => setEditing('new')}>
            {t('stock.suppliers.add')}
          </Button>
        ) : null}
      </Toolbar>
      {body}
      <Dialog
        open={editing !== null}
        onOpenChange={(open) => (open ? undefined : setEditing(null))}
        size="md"
        dirty="auto"
        title={editing === 'new' || editing === null ? t('stock.suppliers.addTitle') : t('stock.suppliers.editTitle', { name: editing.name })}
        description={t('stock.suppliers.hint')}
      >
        {editing !== null ? <SupplierForm supplier={editing === 'new' ? undefined : editing} onDone={() => setEditing(null)} /> : null}
      </Dialog>
    </div>
  );
}

function SupplierForm({ supplier, onDone }: { readonly supplier: StockSupplier | undefined; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const venue = useCurrentVenue();
  const create = useCreateSupplier(venue.organizationId, venue.id);
  const update = useUpdateSupplier(venue.organizationId, venue.id);
  const [name, setName] = useState(supplier?.name ?? '');
  const [phone, setPhone] = useState(supplier?.phone ?? '');
  const [email, setEmail] = useState(supplier?.email ?? '');
  const [note, setNote] = useState(supplier?.note ?? '');
  const [archived, setArchived] = useState(supplier?.archived ?? false);
  const [errors, setErrors] = useState<{ name?: string; phone?: string; email?: string; form?: string }>({});

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const next: typeof errors = {};
    if (name.trim() === '') next.name = t('stock.suppliers.nameRequired');
    if (phone.trim() !== '' && phone.trim().length < 3) next.phone = t('stock.suppliers.phoneInvalid');
    if (email.trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = t('stock.suppliers.emailInvalid');
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    const fields = { name: name.trim(), phone: phone.trim() || null, email: email.trim() || null, note: note.trim() || null };
    if (supplier === undefined) {
      create.mutate(fields, {
        onSuccess: (saved) => {
          show({ tone: 'ok', title: t('stock.suppliers.added', { name: saved.name }) });
          onDone();
        },
        onError: (failure) => setErrors({ form: problem.text(failure, { fallback: t('stock.suppliers.refused') }) }),
      });
      return;
    }
    const patch: UpdateStockSupplierRequest = {};
    if (fields.name !== supplier.name) patch.name = fields.name;
    if (fields.phone !== supplier.phone) patch.phone = fields.phone;
    if (fields.email !== supplier.email) patch.email = fields.email;
    if (fields.note !== supplier.note) patch.note = fields.note;
    if (archived !== supplier.archived) patch.archived = archived;
    if (Object.keys(patch).length === 0) {
      setErrors({ form: t('stock.editor.noChanges') });
      return;
    }
    update.mutate(
      { supplierId: supplier.id, body: patch },
      {
        onSuccess: (saved) => {
          show({ tone: 'ok', title: t('stock.suppliers.saved', { name: saved.name }) });
          onDone();
        },
        onError: (failure) => setErrors({ form: problem.text(failure, { fallback: t('stock.suppliers.refused') }) }),
      },
    );
  }

  return (
    <form className="console-form" aria-label={supplier === undefined ? t('stock.suppliers.addTitle') : t('stock.suppliers.editTitle', { name: supplier.name })} noValidate onSubmit={submit}>
      {errors.form !== undefined ? (
        <Notice tone="danger" role="alert">
          {errors.form}
        </Notice>
      ) : null}
      <TextField label={t('stock.suppliers.name')} required autoComplete="off" value={name} onChange={(event) => setName(event.target.value)} error={errors.name} />
      <div className="console-form__row">
        <TextField label={t('stock.suppliers.phone')} showOptional type="tel" autoComplete="off" value={phone} onChange={(event) => setPhone(event.target.value)} error={errors.phone} />
        <TextField label={t('stock.suppliers.email')} showOptional type="email" autoComplete="off" spellCheck={false} value={email} onChange={(event) => setEmail(event.target.value)} error={errors.email} />
      </div>
      <TextField label={t('stock.suppliers.note')} showOptional autoComplete="off" value={note} placeholder={t('stock.suppliers.notePlaceholder')} onChange={(event) => setNote(event.target.value)} />
      {supplier !== undefined ? <Switch checked={archived} onCheckedChange={setArchived} label={t('stock.suppliers.archive')} /> : null}
      <div className="console-form__actions console-form__actions--end">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={create.isPending || update.isPending}>
          {supplier === undefined ? t('stock.suppliers.addSubmit') : t('stock.editor.save')}
        </Button>
      </div>
    </form>
  );
}
